import { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.vectorgrid';
import 'leaflet-geometryutil';
import { useMap } from 'react-leaflet';

const EvacZonesLayer = () => {
  const map = useMap();
  const [zoneColorMap, setZoneColorMap] = useState({});
  const evacLayerRef = useRef(null);

  const statusColorMap = {
    'orders': ['#d73027'],   // Red
    'warnings': ['#edc02b'], // Yellow
  };

  const fetchZoneColors = async () => {
    try {
      const res = await fetch('/incident/api/watchduty/statuses/');
      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const data = await res.json();
      const map = {};
      data.forEach(item => {
        const uid = item.evac_zone.uid_v2;
        const status = item.status;
        const color = statusColorMap[status];
        if (uid && color) map[uid] = [color, 0.4];
      });
      return map;
    } catch (error) {
      console.error('Failed to fetch zone colors:', error);
      // Return empty map on failure so map renders without color info
      return {};
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchStatuses = async () => {
      const map = await fetchZoneColors();
      if (isMounted) setZoneColorMap(map);
    };

    fetchStatuses();

    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!map) return;

    if (evacLayerRef.current) {
      map.removeLayer(evacLayerRef.current);
    }

    const evacLayer = L.vectorGrid.protobuf(
      '/incident/api/watchduty/evaczone/tiles/{z}/{x}/{y}.pbf',
      {
        vectorTileLayerStyles: {
          zones: (properties, zoom) => {
            const id = properties.zone_uid_v2;
            const zoneStyle = zoneColorMap[id];
            return {
              fill: true,
              fillColor: zoneStyle?.[0] ?? "#000000",
              fillOpacity: zoneStyle?.[1] ?? 0,
              color: '#555',
              weight: 1,
              opacity: zoneStyle?.[1] ?? 0,
            };
          },
          labels: {
            fill: false,
            stroke: false,
          },
        },
        interactive: true,
        maxNativeZoom: 10,
        maxZoom: 16,
      }
    );

    evacLayer.on('mouseover', e => {
      const props = e.layer?.properties;
      const latlng = e.latlng;
      const id = props?.zone_uid_v2;
      const zoneStyle = zoneColorMap[id];
      const opacity = zoneStyle?.[1] ?? 0;
      if (props?.zone_name && latlng && opacity > 0) {
        const tooltip = L.tooltip({
          permanent: false,
          direction: 'top',
          className: 'evac-tooltip',
          offset: [0, -10]
        })
          .setContent(props.zone_name)
          .setLatLng(latlng)
          .addTo(map);

        evacLayer.once('mouseout', () => {
          map.removeLayer(tooltip);
        });
      }
    });

    evacLayer.addTo(map);
    evacLayerRef.current = evacLayer;

    return () => {
      if (map && evacLayerRef.current) {
        map.removeLayer(evacLayerRef.current);
      }
    };
  }, [map, zoneColorMap]);

  return null;
};

export default EvacZonesLayer;
